import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import CallIcon from "@mui/icons-material/Call";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

// Each contact role gets its own dot colour so a glance tells you whose
// number is missing on each search hit.
const CONTACT_COLORS = {
  operator:    { idle: "#a78bfa", hover: "#8b5cf6" }, // violet
  electrician: { idle: "#fbbf24", hover: "#f59e0b" }, // amber
  secretary:   { idle: "#34d399", hover: "#10b981" }, // mint/teal
  customer:    { idle: "#60a5fa", hover: "#2563eb" }, // blue
};

const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

// Single contact line — [📞] Label  Name  Number
// tel: link when callable; stopPropagation so dialing doesn't fire the row's
// onClick (which navigates to the company devices page).
const ContactRow = ({ label, name, phone, colors }) => {
  const callable = Boolean(phone);
  return (
    <Box
      onClick={callable ? (e) => e.stopPropagation() : undefined}
      component={callable ? "a" : "div"}
      href={callable ? telHref(phone) : undefined}
      sx={{
        // Mobile: flex name column so it fits any viewport.
        // Desktop: fixed widths so the table aligns across rows.
        display: "grid",
        gridTemplateColumns: {
          xs: "20px 78px minmax(0, 1fr) auto",
          md: "20px 90px 160px 120px",
        },
        alignItems: "center",
        columnGap: 1.25,
        borderRadius: "4px",
        py: 0.25,
        minWidth: 0,
        textDecoration: "none",
        color: "inherit",
        ...(callable && {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.04)",
            "& .row-call-btn": { backgroundColor: colors.hover },
          },
        }),
      }}
    >
      <Box
        className="row-call-btn"
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: callable ? colors.idle : "rgba(0,0,0,0.08)",
          transition: "background-color 0.15s",
          "& .MuiSvgIcon-root": {
            color: callable ? "#ffffff" : "rgba(0,0,0,0.3)",
            fontSize: 11,
          },
        }}
      >
        <CallIcon />
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.6875rem",
          color: name ? "text.primary" : "text.disabled",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name || "—"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.6875rem",
          fontFamily: "monospace",
          fontWeight: 500,
          color: phone ? "text.primary" : "text.disabled",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {phone || "—"}
      </Typography>
    </Box>
  );
};

// The 3-row contact block dropped into the table's Contact column.
const ResultContactCell = ({ r }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
    <ContactRow label="Operator"    name={r.operator_name}  phone={r.operator_phone}            colors={CONTACT_COLORS.operator} />
    <ContactRow label="Electrician" name={null}             phone={r.local_electrician_number}  colors={CONTACT_COLORS.electrician} />
    <ContactRow label="Secretary"   name={r.secretary_name} phone={r.secretary_phone}           colors={CONTACT_COLORS.secretary} />
  </Box>
);

// Mobile card — one tap target per result, with the 3 contact rows underneath
// company + device info. The card itself navigates; individual call rows
// dial via tel: (stopPropagation already inside ContactRow).
const ResultCard = ({ r, onOpen }) => (
  <Paper
    variant="outlined"
    onClick={() => onOpen(r)}
    sx={{
      p: 1.25,
      cursor: "pointer",
      minWidth: 0,
      overflow: "hidden",
      backgroundColor: "#f4f8fc",
      borderColor: "#dbe6f0",
      transition: "border-color 0.15s, background-color 0.15s",
      "&:hover": { borderColor: "primary.main", backgroundColor: "#e8f0fa" },
    }}
  >
    <Stack direction="row" alignItems="baseline" spacing={0.75} mb={0.5} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontFamily: "monospace",
          color: "primary.main",
          fontWeight: 700,
          fontSize: "0.6875rem",
          flexShrink: 0,
        }}
      >
        {r.manufacturing_id != null ? `#${r.manufacturing_id}` : "—"}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: "0.8125rem",
          lineHeight: 1.2,
          wordBreak: "break-word",
          minWidth: 0,
          flex: 1,
        }}
      >
        {r.company_name || "—"}
      </Typography>
    </Stack>

    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "6px",
          backgroundColor: "primary.light",
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <RouterIcon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            display: "block",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {r.device_name || "(no name)"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            color: "text.secondary",
            fontSize: "0.6875rem",
            whiteSpace: "nowrap",
          }}
        >
          {r.device_imei}
        </Typography>
      </Box>
    </Stack>

    <Divider sx={{ my: 0.75 }} />

    <ResultContactCell r={r} />
  </Paper>
);

export default function SearchPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = input.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setTouched(true);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      try {
        const { data } = await api.get(`/devices/quick-search`, {
          params: { q, limit: 50 },
        });
        if (reqId !== reqIdRef.current) return;
        setResults(data.data.items || []);
      } catch {
        if (reqId === reqIdRef.current) setResults([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [input]);

  const goToDevice = (r) => {
    if (r.company_id) navigate(`/companies/${r.company_id}/devices`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: { md: 1 },
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        mb={1}
      >
        <Breadcrumbs
          separator="›"
          sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>
            Home
          </Typography>
          <Typography
            color="text.primary"
            sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
          >
            Search
          </Typography>
        </Breadcrumbs>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: { md: 1 },
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* Sticky search row at the top, same pattern as Companies page. */}
        <Box
          sx={{
            p: 1,
            display: "flex",
            gap: 1,
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search devices by IMEI, name, or company…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ) : null,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              display: { xs: "none", md: "block" },
            }}
          >
            {results.length} result{results.length === 1 ? "" : "s"}
          </Typography>
        </Box>

        {/* Mobile: scrollable card list. Desktop: the dense Fiori table. */}
        {isMobile ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
            {!touched && (
              <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <SearchIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
                <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                  Type 2+ characters to search devices by IMEI, name, or company.
                </Typography>
              </Box>
            )}
            {touched && loading && results.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {touched && !loading && results.length === 0 && input.trim().length >= 2 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                  No devices match “{input.trim()}”.
                </Typography>
              </Box>
            )}
            <Stack spacing={1}>
              {results.map((r) => (
                <ResultCard key={r.device_id} r={r} onOpen={goToDevice} />
              ))}
            </Stack>
          </Box>
        ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              // Fixed layout so every column has an identical width on every
              // row — that's the only way the icon/label/name/phone grid
              // inside the Contact cell lines up vertically across rows.
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                py: 1.5,
                px: 2,
              },
              "& tbody tr:last-child .MuiTableCell-root": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 90, whiteSpace: "nowrap" }}>Mfg ID</TableCell>
                <TableCell sx={{ width: 200 }}>Company</TableCell>
                <TableCell sx={{ width: 470 }}>Contact</TableCell>
                <TableCell>Device</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!touched && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <SearchIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
                    <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                      Type 2+ characters to search devices by IMEI, name, or company.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {touched && loading && results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {touched && !loading && results.length === 0 && input.trim().length >= 2 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No devices match “{input.trim()}”.
                  </TableCell>
                </TableRow>
              )}
              {results.map((r) => (
                <TableRow
                  key={r.device_id}
                  hover
                  onClick={() => goToDevice(r)}
                  sx={{ cursor: "pointer", verticalAlign: "middle" }}
                >
                  <TableCell sx={{ fontFamily: "monospace", color: "primary.main" }}>
                    {r.manufacturing_id != null ? `#${r.manufacturing_id}` : "—"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                  >
                    {r.company_name || "—"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ResultContactCell r={r} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          backgroundColor: "primary.light",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <RouterIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8125rem",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.device_name || "(no name)"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                            fontSize: "0.6875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.device_imei}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
